import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { getBranches, type Branch } from '../lib/api'
import { useAuth } from './AuthContext'

// ── Branch view-filter ────────────────────────────────────────────────────
// A user only ever sees the branches they belong to (org admins see all org
// branches — the backend's GET /branches enforces this). Among that accessible
// set, the sidebar lets the user toggle individual branches off to narrow what
// every branch-scoped list in the app shows. Two invariants:
//
//   • By default all accessible branches are ENABLED. A branch the user newly
//     gains access to shows up enabled automatically — we persist the DISABLED
//     set, not the enabled one, so "new = on" needs no migration.
//   • At least one branch stays enabled. Disabling the final enabled branch is
//     a no-op (an empty filter would blank every list, which is never useful).
//
// The choice is a per-user, per-device view preference (localStorage). It is
// not security: access is enforced server-side by membership. This only decides
// which of *your* branches you're currently looking at.

interface BranchFilterValue {
  /** Branches the current user can access (membership-scoped by the backend). */
  branches:   Branch[]
  loading:    boolean
  /** IDs currently toggled on. Always ⊆ branches and (when branches exist) ≥1. */
  enabledIds: number[]
  /** True when a resource's branch belongs to the enabled set. A null/undefined
   *  branch id (resource not assigned to any branch) is always considered
   *  visible — the filter narrows by branch, it doesn't hide unassigned items. */
  isEnabled:  (branchId?: number | null) => boolean
  /** Flip one branch on/off. Disabling the last enabled branch is ignored. */
  toggle:     (branchId: number) => void
  /** Re-fetch the accessible branch set (e.g. after creating/joining one). */
  reload:     () => void
}

const BranchFilterContext = createContext<BranchFilterValue | null>(null)

const STORAGE_PREFIX = 'wetook_branch_disabled_'

function readDisabled(userId: number): Set<number> {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${userId}`)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? new Set(arr.filter((n): n is number => typeof n === 'number')) : new Set()
  } catch {
    return new Set()
  }
}

function writeDisabled(userId: number, ids: Set<number>) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify([...ids]))
  } catch {
    /* storage full / unavailable — the filter just won't persist this device */
  }
}

export function BranchFilterProvider({ children }: { children: ReactNode }) {
  const { user, hasSession } = useAuth()
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading]   = useState(true)
  // Persisted "off" set. Kept as state so toggles re-render; mirrored to
  // localStorage on every change.
  const [disabled, setDisabled] = useState<Set<number>>(new Set())
  const loadedForUser = useRef<number | null>(null)

  const reload = useCallback(() => {
    if (!hasSession) {
      setBranches([])
      setLoading(false)
      return
    }
    setLoading(true)
    getBranches()
      .then(r => setBranches(r.data))
      .catch(() => setBranches([]))
      .finally(() => setLoading(false))
  }, [hasSession])

  // Load the persisted disabled set whenever the signed-in user changes.
  useEffect(() => {
    if (!user) {
      setDisabled(new Set())
      loadedForUser.current = null
      return
    }
    if (loadedForUser.current !== user.id) {
      loadedForUser.current = user.id
      setDisabled(readDisabled(user.id))
    }
  }, [user])

  // Fetch accessible branches once we have a session.
  useEffect(() => { reload() }, [reload])

  // Reconcile the disabled set against what the user can actually access:
  // drop ids that are no longer accessible (revoked membership / deleted
  // branch) so a stale id can't silently keep filtering. Never persist ids
  // outside the accessible set.
  useEffect(() => {
    if (!user || loading) return
    const accessible = new Set(branches.map(b => b.id))
    setDisabled(prev => {
      const next = new Set([...prev].filter(id => accessible.has(id)))
      if (next.size !== prev.size) writeDisabled(user.id, next)
      return next
    })
  }, [branches, loading, user])

  const enabledIds = useMemo(
    () => branches.map(b => b.id).filter(id => !disabled.has(id)),
    [branches, disabled],
  )

  const enabledSet = useMemo(() => new Set(enabledIds), [enabledIds])

  const isEnabled = useCallback(
    (branchId?: number | null) => {
      if (branchId == null) return true          // unassigned → not filtered out
      return enabledSet.has(branchId)
    },
    [enabledSet],
  )

  const toggle = useCallback(
    (branchId: number) => {
      if (!user) return
      setDisabled(prev => {
        const next = new Set(prev)
        if (next.has(branchId)) {
          next.delete(branchId)               // re-enable
        } else {
          // Disable — but only if it leaves at least one branch enabled.
          const enabledCount = branches.filter(b => !next.has(b.id)).length
          if (enabledCount <= 1) return prev
          next.add(branchId)
        }
        writeDisabled(user.id, next)
        return next
      })
    },
    [user, branches],
  )

  const value = useMemo<BranchFilterValue>(
    () => ({ branches, loading, enabledIds, isEnabled, toggle, reload }),
    [branches, loading, enabledIds, isEnabled, toggle, reload],
  )

  return (
    <BranchFilterContext.Provider value={value}>
      {children}
    </BranchFilterContext.Provider>
  )
}

export function useBranchFilter(): BranchFilterValue {
  const ctx = useContext(BranchFilterContext)
  if (!ctx) throw new Error('useBranchFilter must be used inside BranchFilterProvider')
  return ctx
}
